import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Badge from '../common/Badge';
import Button from '../common/Button';
import Input from '../common/Input';
import Modal from '../common/Modal';
import { SectionCard, Spinner } from '../common/PageHeader';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../hooks/useConfirm';
import { queryKeys } from '../../lib/queryClient';
import { api, errorMessage } from '../../services/api';
import { ExpenseCategory } from '../../types';
import { CATEGORY_NAME_PATTERN, v } from '../../utils/validation';

const ICONS = [
  'fa-receipt', 'fa-robot', 'fa-laptop-code', 'fa-cloud', 'fa-wifi', 'fa-mobile-screen', 'fa-building', 'fa-bolt',
  'fa-computer', 'fa-paperclip', 'fa-plane', 'fa-car', 'fa-utensils', 'fa-bullhorn', 'fa-scale-balanced', 'fa-landmark',
  'fa-building-columns', 'fa-graduation-cap', 'fa-gift', 'fa-shield-halved', 'fa-screwdriver-wrench', 'fa-box', 'fa-ellipsis',
];
const COLORS = ['#0056d6', '#0088ff', '#0ea5e9', '#0891b2', '#0d9488', '#16a34a', '#e06a00', '#ff9a1a', '#db2777', '#9333ea', '#7c3aed', '#b91c1c', '#475569', '#6b788c'];

export default function CategoriesTab() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const { confirm, ConfirmDialog } = useConfirm();
  const canManage = hasPermission('expenses:manage');

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<ExpenseCategory | null>(null);
  const [form, setForm] = useState({ name: '', description: '', icon: ICONS[0], color: COLORS[0] });
  const [nameError, setNameError] = useState('');
  const [descError, setDescError] = useState('');

  const { data: categories = [], isLoading } = useQuery({
    queryKey: queryKeys.categories(),
    queryFn: () => api.get<ExpenseCategory[]>('/expense-categories'),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.categories() });
  const saveMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      editing ? api.patch(`/expense-categories/${editing.id}`, payload) : api.post('/expense-categories', payload),
    onSuccess: invalidate,
  });
  const toggleMutation = useMutation({
    mutationFn: (c: ExpenseCategory) => api.patch(`/expense-categories/${c.id}`, { isActive: !c.isActive }),
    onSuccess: invalidate,
  });
  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/expense-categories/${id}`),
    onSuccess: invalidate,
  });

  function open(c?: ExpenseCategory) {
    setEditing(c ?? null);
    setNameError('');
    setDescError('');
    setForm(c ? { name: c.name, description: c.description ?? '', icon: c.icon, color: c.color } : { name: '', description: '', icon: ICONS[0], color: COLORS[0] });
    setShowModal(true);
  }

  async function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    const name = form.name.trim().toLowerCase();
    const nameErr =
      v.text(form.name, 'Category name', {
        required: true, min: 2, max: 80, pattern: CATEGORY_NAME_PATTERN,
        patternMessage: 'Category name must start with a letter and use only letters, numbers and & , . / ( ) -',
      }) ?? (categories.some((c) => c.id !== editing?.id && c.name.toLowerCase() === name) ? 'A category with this name already exists' : undefined);
    const descErr = v.text(form.description, 'Description', { max: 255 });
    setNameError(nameErr ?? '');
    setDescError(descErr ?? '');
    if (nameErr || descErr) return;
    try {
      await saveMutation.mutateAsync({ ...form, name: form.name.trim(), description: form.description.trim() });
      showToast(editing ? 'Category updated' : 'Category added', 'success');
      setShowModal(false);
    } catch (err) {
      showToast(errorMessage(err, 'Failed to save category'), 'error');
    }
  }

  async function remove(c: ExpenseCategory) {
    const yes = await confirm({ title: 'Delete Category', message: `Delete "${c.name}"?`, confirmText: 'Delete', variant: 'danger' });
    if (!yes) return;
    try {
      await deleteMutation.mutateAsync(c.id);
      showToast('Category deleted', 'success');
    } catch (err) {
      showToast(errorMessage(err, 'Failed to delete'), 'error');
    }
  }

  if (isLoading) return <Spinner />;

  return (
    <>
      <SectionCard
        title={`Expense Categories (${categories.length})`}
        bodyClassName="p-0"
        actions={canManage && <Button size="sm" variant="primary" icon="fa-plus" onClick={() => open()}>Add Category</Button>}
      >
        <ul className="divide-y divide-ink-100">
          {categories.map((c) => (
            <li key={c.id} className={`flex items-center gap-3 px-4 py-3 ${c.isActive ? '' : 'opacity-60'}`}>
              <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${c.color}1f`, color: c.color }}>
                <i className={`fas ${c.icon}`} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-ink-900">
                  {c.name}
                  {c.systemCode && <Badge variant="primary" size="sm" className="ml-2">Payroll</Badge>}
                  {!c.isActive && <Badge variant="secondary" size="sm" className="ml-2">Inactive</Badge>}
                </p>
                {c.description && <p className="text-xs text-ink-500 truncate">{c.description}</p>}
              </div>
              {canManage && (
                <div className="flex items-center gap-2 shrink-0">
                  <button className="px-3 py-1 bg-ink-100 text-ink-700 text-xs rounded hover:bg-ink-200" onClick={() => open(c)}>Edit</button>
                  {!c.systemCode && (
                    <>
                      <button className="px-3 py-1 bg-white border border-ink-200 text-ink-600 text-xs rounded hover:bg-ink-50"
                        onClick={() => toggleMutation.mutate(c, { onError: (err) => showToast(errorMessage(err), 'error') })}>
                        {c.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button className="px-2.5 py-1 bg-red-50 text-red-600 text-xs rounded hover:bg-red-100" onClick={() => remove(c)} aria-label={`Delete ${c.name}`}>
                        <i className="fas fa-trash-can" />
                      </button>
                    </>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      </SectionCard>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editing ? 'Edit Category' : 'Add Category'} size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleSubmit as any} disabled={saveMutation.isPending}>{saveMutation.isPending ? 'Saving…' : 'Save'}</Button>
          </div>
        }>
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <Input label="Name" required value={form.name} maxLength={80} error={nameError}
            onChange={(e) => { setForm((p) => ({ ...p, name: e.target.value })); setNameError(''); }} />
          <Input label="Description" value={form.description} maxLength={255} error={descError}
            onChange={(e) => { setForm((p) => ({ ...p, description: e.target.value })); setDescError(''); }} />
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.06em] text-ink-600 mb-1.5">Icon</p>
            <div className="flex flex-wrap gap-1.5">
              {ICONS.map((icon) => (
                <button key={icon} type="button" onClick={() => setForm((p) => ({ ...p, icon }))} aria-label={icon}
                  className={`w-9 h-9 rounded-md border flex items-center justify-center ${form.icon === icon ? 'border-brand-600 bg-brand-50 text-brand-700' : 'border-ink-200 text-ink-500 hover:bg-ink-50'}`}>
                  <i className={`fas ${icon}`} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.06em] text-ink-600 mb-1.5">Colour</p>
            <div className="flex flex-wrap gap-2">
              {COLORS.map((color) => (
                <button key={color} type="button" onClick={() => setForm((p) => ({ ...p, color }))} aria-label={color}
                  className={`w-7 h-7 rounded-full border-2 ${form.color === color ? 'border-ink-900 scale-110' : 'border-white'} shadow-soft`}
                  style={{ backgroundColor: color }} />
              ))}
            </div>
          </div>
        </form>
      </Modal>

      <ConfirmDialog />
    </>
  );
}
